<template>
  <div ref="container" class="zoom-masonry">
    <div class="sticky">
      <div
        class="el"
        :class="{ 'has-zoomed-video': isFullyZoomed[index] && zoomImage.fullVideo }"
        :data-scale="zoomImage.scale"
        v-for="(zoomImage, index) in images"
        :key="index"
      >
        <div class="imageContainer">
          <!-- Video -->
          <WidgetVideoMasonry
            v-if="zoomImage.fullVideo || zoomImage.videoPreview"
            :ref="(el) => setVideoRef(el, index)"
            class="is-cover"
            :videoPreview="zoomImage.videoPreview"
            :fullVideo="zoomImage.fullVideo"
            :isFullyZoomed="isFullyZoomed[index]"
          />
          <!-- Image -->
          <WidgetPicture
            v-else-if="zoomImage.image"
            class="is-cover"
            :image="{ custom: zoomImage.image.responsiveImage }"
            :lazyLoad="false"
            :decodeMethod="'async'"
            :alt="
              zoomImage.image?.responsiveImage?.alt !== null
                ? zoomImage.image.responsiveImage.alt
                : runtimeConfig.public.siteName
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const runtimeConfig = useRuntimeConfig()

const container = ref(null)

const props = defineProps({
  images: {
    type: Array,
    default: () => [],
  },
})

const isFullyZoomed = ref({})
const videoRefs = ref({})
const lastScrollProgress = ref({})

const setVideoRef = (el, index) => {
  if (el) videoRefs.value[index] = el
}
const scaleTargetOnScroll = (target, scaleMax = 4, index = null) => {
  // Controlla se questo elemento contiene un video
  const hasVideo = props.images[index]?.fullVideo || props.images[index]?.videoPreview
  
  if (hasVideo) {
    // Timeline con pausa di 2 secondi per i video
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container.value,
        start: 'top top',
        end: 'bottom bottom',
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          // Determina la direzione dello scroll
          const currentProgress = self.progress
          const lastProgress = lastScrollProgress.value[index] || 0
          const isScrollingBackward = currentProgress < lastProgress
          lastScrollProgress.value[index] = currentProgress

          // Controlla se è completamente zoomato (50% di progresso - quando raggiunge il full scale)
          if (index !== null && currentProgress > 0.50 && currentProgress < 0.95) {
            isFullyZoomed.value[index] = true
          } else if (index !== null) {
            
            // Controlla se il video sta DAVVERO suonando controllando l'elemento video
            const videoComponent = videoRefs.value[index]
            const fullVideoElement = videoComponent?.fullVideoElement
            const isVideoActuallyPlaying = fullVideoElement && !fullVideoElement.paused && !fullVideoElement.ended
            
            // Se scrolla indietro E il video sta suonando, resetta IMMEDIATAMENTE al preview
            if (isScrollingBackward && isVideoActuallyPlaying) {
              if (videoComponent?.resetFullVideo) {
                videoComponent.resetFullVideo()
              }
              isFullyZoomed.value[index] = false
              return // Esci subito per evitare altre logiche
            }
            
            isFullyZoomed.value[index] = false
            
            // Se scrolla indietro e il video non è in play ed è sotto il 45%, resetta al preview (con ritardo)
            if (isScrollingBackward && !isVideoActuallyPlaying && currentProgress < 0.45) {
              if (videoComponent?.resetFullVideo) {
                videoComponent.resetFullVideo()
              }
            }
          }
        }
      },
    })

    // Scale animation con pausa 2 secondi a full scale per video
    tl.to(target, {
      scale: scaleMax * 0.8, // Scale a 80% del max
      ease: "none",
      duration: 0.4
    })
    .to(target, {
      scale: scaleMax, // Completa il full scale
      ease: "none", 
      duration: 0.1
    })
    .to(target, {
      scale: scaleMax, // Mantieni il full scale per 2 secondi
      ease: "none",
      duration: 0.5 // Questo crea la pausa di 2 secondi nello scroll
    })
  } else {
    // Scaling semplice e diretto per le immagini (senza pausa)
    gsap.to(target, {
      scale: scaleMax,
      ease: "none",
      scrollTrigger: {
        trigger: container.value,
        start: 'top top',
        end: 'bottom bottom',
        scrub: true,
        invalidateOnRefresh: true,
      }
    })
  }
}

onMounted(() => {
  // https://chatgpt.com/g/g-2DQzU5UZl-code-copilot/c/66fe92bc-7bfc-8001-b95a-8ceb229cc6d9
  // Inizializza isFullyZoomed e lastScrollProgress per tutti i media
  props.images.forEach((image, index) => {
    isFullyZoomed.value[index] = false
    lastScrollProgress.value[index] = 0
  })
  
  if (container.value) {
    const targets = container.value.querySelectorAll('[data-scale]')
    targets.forEach((target, index) => {
      const scaleValue = target.getAttribute('data-scale')
      scaleTargetOnScroll(target, scaleValue, index)
    })
  }
})
</script>

<style lang="scss">
@import './style.scss';
</style>
